import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Message,
    Segment,
    SegmentContent,
    SegmentHeader,
} from '@/platform/ui';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import CommunicationSection from './CommunicationSection';

const metaLine = transaction =>
    [
        transaction.document?.name,
        `Aangemaakt: ${localDate(transaction.created_at)}`,
        transaction.expires_at &&
            `Verloopt: ${localDate(transaction.expires_at)}`,
        transaction.signed_at &&
            `Ondertekend: ${localDate(transaction.signed_at)}`,
    ]
        .filter(Boolean)
        .join(' · ');

// Waiting for document, waiting for signer and in progress: still live at Signhost.
const OPEN_STATUSES = [5, 10, 20];
const SIGNED = 30;

const SignhostTransactionCard = ({ transaction }) => {
    const { auth, employee } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();
    const canCancel =
        auth?.permissions?.includes('view signhost') &&
        OPEN_STATUSES.includes(transaction.status);
    // Signed, but the signed document never arrived.
    const canRefetch =
        auth?.permissions?.includes('refetch signed signhost transaction') &&
        transaction.status === SIGNED &&
        !transaction.document?.id;

    const [refetching, setRefetching] = useState(false);

    const handleRefetch = () =>
        router.post(
            `/customers/${employee.customer_id}/employees/${employee.id}/authorisations/${transaction.id}/refetch`,
            {},
            {
                reset: ['employee', 'navigation'],
                preserveScroll: true,
                onStart: () => setRefetching(true),
                onFinish: () => setRefetching(false),
            }
        );

    const handleCancel = () =>
        confirm({
            title: 'Machtiging annuleren?',
            description:
                'De machtiging wordt bij Signhost geannuleerd en kan niet meer worden ondertekend. De bijbehorende communicatie wordt verwijderd. Dit kun je niet ongedaan maken.',
            confirmLabel: 'Machtiging annuleren',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/authorisations/${transaction.id}`,
                    { preserveScroll: true, onFinish: close }
                );
            },
        });

    return (
        <Segment>
            <SegmentHeader
                header={
                    <span className='inline-flex items-center gap-2'>
                        Machtiging
                        {transaction.status_label && (
                            <Badge color={transaction.status_color}>
                                {transaction.status_label}
                            </Badge>
                        )}
                    </span>
                }
                subheader={metaLine(transaction)}
            >
                {canRefetch && (
                    <Button
                        type='button'
                        loading={refetching}
                        onClick={handleRefetch}
                    >
                        <ArrowPathIcon className='size-4' />
                        Document opnieuw ophalen
                    </Button>
                )}
                {canCancel && (
                    <Button
                        type='button'
                        variant='danger'
                        aria-label='Machtiging annuleren'
                        onClick={handleCancel}
                    >
                        <TrashIcon className='size-4' />
                    </Button>
                )}
            </SegmentHeader>
            <SegmentContent>
                {transaction.rejection_reason && (
                    <Message
                        variant='error'
                        title='Reden van weigering'
                        content={transaction.rejection_reason}
                        className='mb-4'
                    />
                )}

                <CommunicationSection
                    communication={transaction.communication}
                    emptyText='Er is geen communicatie voor deze machtiging.'
                />
            </SegmentContent>
        </Segment>
    );
};

export default SignhostTransactionCard;
